import clsx from 'clsx';
import parse, {
  type DOMNode,
  Element,
  type HTMLReactParserOptions,
  attributesToProps,
  domToReact,
} from 'html-react-parser';
import Image from 'next/image';
import parseStyle from 'style-to-js';

import AnimatedNumber from '@commons/components/AnimatedNumber';
import { type ButtonIcon, ButtonLink, type ButtonVariant } from '@commons/components/Button';
import Link, { type LinkProps } from '@commons/components/Link';
import { List, ListItem, type ListVariant } from '@commons/components/List';
import Quote from '@commons/components/Quote';
import Separator from '@commons/components/Separator';
import { Table, getTableTypeFromClasses } from '@commons/components/Table';
import { Text, type TextLevel, type TextNode } from '@commons/components/Text';
import VideoPlayer from '@commons/components/VideoPlayer';
import BaseContainer, { type BaseContainerProps } from '@commons/containers/BaseContainer';
import { asAbsoluteURL } from '@commons/helpers/url';
import type { FontWeight } from '@commons/types/styles';
import { NEXT_IMAGE_QUALITY } from '@commons/utils/constants';

import AudioPlayer from '../AudioPlayer';
import Typewritter from '../Typewritter';
import { getTemplate } from './templates';

// The supported HTML nodes and options from CKEditor.

const headings = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

const textNodes = ['p', ...headings];

const textLevels = [
  ...textNodes,
  'display-1',
  'display-2',
  'label-lg',
  'label-md',
  'label-sm',
  'body-lg',
  'body-md',
  'body-sm',
  'body-xs',
];

const listNodes = ['ul', 'ol'];

/**
 * The supported font weights classes from CKEditor.
 */

export const fontWeights = ['fw-light', 'fw-normal', 'fw-medium', 'fw-semibold', 'fw-bold'];

/**
 * The supported text alignments classes from CKEditor.
 */

export const textAlignments = [
  'text-align-left',
  'text-align-center',
  'text-align-right',
  'text-align-justify',
];

/**
 * Renders a React component from a HTML node.
 *
 * This function is called recursively to ensure that the whole tree is rendered.
 *
 * @param node - The node to render.
 * @param options - The parsing options.
 *
 * @returns A tree of React component.
 *
 * @see https://github.com/remarkablemark/html-react-parser
 */

const renderReactFromHtml = (node: DOMNode, options: HTMLReactParserOptions) => {
  if (node instanceof Element && node.attribs) {
    const { name, attribs, prev, next } = node;

    // We need to cast children because the types of this library are weird.
    const children = node.children as DOMNode[];

    // Prepare an array of classes.
    const classes: string[] = attribs.class?.split(' ') ?? [];

    // Parse the inline styles to an object (e.g. `marginLeft` instead of `margin-left`).
    const parsedStyles = parseStyle(attribs.style ?? '');

    // Text nodes.
    if (textNodes.includes(name)) {
      let size: string | undefined;
      let level: TextLevel | undefined;
      let weight: FontWeight | undefined;
      let isUppercase = false;
      const additionalClasses: string[] = [];

      // Go through each classes and check if a property needs to be added.
      classes.forEach((className) => {
        // Check if the class is a level.
        if (textLevels.includes(className)) level = className as TextLevel;

        // Check if the class is a custom font size.
        if (className.startsWith('fs-')) {
          size = className.replace('fs-', '');
          parsedStyles.fontSize = size;
        }

        // Check if the class is a font weight.
        if (fontWeights.includes(className)) {
          weight = className.replace('fw-', '') as FontWeight;
        }

        // Checks if the text has an alignment.

        if (className.startsWith('text-align')) {
          additionalClasses.push(className);
        }
        // Checks if the text should be uppercase.
        if (className === 'uppercase') isUppercase = true;

        // Checks if the text is hidden on different BreakPoints.
        if (className.startsWith('hidden')) {
          additionalClasses.push(className);
        }
      });
      // Checks if the margin bottom needs to be removed.
      const noMarginBottom = next === null || attribs.class?.includes('mb-0');

      return (
        <Text
          as={name as TextNode}
          id={attribs.id}
          level={level}
          weight={weight}
          transform={isUppercase ? 'uppercase' : undefined}
          marginBottom={noMarginBottom ? 'none' : undefined}
          className={clsx(additionalClasses)}
          style={parsedStyles}
        >
          {domToReact(children, options)}
        </Text>
      );
    }

    // Span nodes, used for special use cases.
    if (name === 'span') {
      // Animated number.
      if (classes.includes('animated-number')) {
        const content = domToReact(children, options);

        // Make sure we have a string and not an HTML element.
        if (typeof content === 'string') {
          const number = parseFloat(content);
          return <AnimatedNumber end={number} />;
        }
      }

      if (classes.includes('typewritter')) {
        const content = domToReact(children, options);

        // Make sure we have a string and not an HTML element.
        if (typeof content === 'string') {
          return <Typewritter text={content} />;
        }
      }

      // Uppercased text.
      if (classes.includes('uppercase')) {
        return (
          <Text as="span" transform="uppercase">
            {domToReact(children, options)}
          </Text>
        );
      }
    }

    // Link nodes.
    if (name === 'a' && attribs.href) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { href, class: linkClasses, ...rest } = attribs;

      let url = href;

      // Check if the link is a media reference.
      if ('data-entity-type' in attribs && attribs['data-entity-type'] === 'media') {
        url = asAbsoluteURL(href);
      }

      // Checks if the link should be rendered as a button.
      let btnVariant: ButtonVariant | undefined;
      if (classes.includes('btn-primary')) btnVariant = 'primary';
      if (classes.includes('btn-secondary')) btnVariant = 'secondary';
      if (classes.includes('btn-tertiary')) btnVariant = 'tertiary';

      if (btnVariant) {
        // Check if the button should be XL.
        const isXL = classes.includes('xl');

        // Check if the button has an icon class.
        let btnIcon: ButtonIcon | undefined;
        const btnIconClass = classes.find((c) => c.startsWith('icon-'));

        if (btnIconClass) {
          btnIcon = btnIconClass.replace('icon-', '') as ButtonIcon;
        }

        return (
          <ButtonLink href={url} xl={isXL} icon={btnIcon} variant={btnVariant}>
            {domToReact(children, options)}
          </ButtonLink>
        );
      }

      // Check if the link has an underline.
      let underline = false;
      if (classes.includes('underline')) underline = true;

      // Check if the link has an arrow.
      let withArrow: LinkProps['withArrow'] = false;
      if (classes.includes('with-arrow-sm')) withArrow = 'sm';
      if (classes.includes('with-arrow-md')) withArrow = 'md';
      if (classes.includes('with-arrow-lg')) withArrow = 'lg';

      return (
        <Link href={url} underline={underline} withArrow={withArrow} {...rest}>
          {domToReact(children, options)}
        </Link>
      );
    }

    // List nodes.
    if (listNodes.includes(name)) {
      let variant: ListVariant = 'dash';

      if (classes.includes('dash')) variant = 'dash';
      if (classes.includes('square')) variant = 'square';
      if (classes.includes('bullet')) variant = 'bullet';
      if (classes.includes('border')) variant = 'border';
      if (classes.includes('checkmark-circle')) variant = 'checkmark-circle';

      if (name === 'ol') variant = 'number';
      const start = name === 'ol' && attribs.start ? Number.parseInt(attribs.start, 10) : undefined;

      return (
        <List variant={variant} start={start} style={parsedStyles}>
          {domToReact(children, options)}
        </List>
      );
    }

    // List item nodes.
    if (name === 'li') {
      return <ListItem style={parsedStyles}>{domToReact(children, options)}</ListItem>;
    }

    // Blockquote nodes.
    if (name === 'blockquote') {
      return (
        <Quote withIcon border="left" paddingLeft="md" marginTop="sm" marginBottom="sm">
          {domToReact(children, options)}
        </Quote>
      );
    }

    // Image nodes.
    if (name === 'img') {
      const { src, alt, width, height } = attribs;

      const absoluteUrl = asAbsoluteURL(src);
      const extension = absoluteUrl.split('.').pop();

      const isBase64 = src.startsWith('data:image');
      if (isBase64) {
        // eslint-disable-next-line @next/next/no-img-element
        return <img src={src} alt={alt} className="w-100" />;
      }

      const isSVG = extension === 'svg';
      if (isSVG) {
        // eslint-disable-next-line @next/next/no-img-element
        return <img src={absoluteUrl} alt={alt} className="marginBottom-xs" />;
      }

      if (width && height) {
        return (
          <Image
            src={absoluteUrl}
            alt={alt}
            title={alt}
            width={parseInt(width)}
            height={parseInt(height)}
            quality={NEXT_IMAGE_QUALITY}
          />
        );
      }

      return null;
    }

    // Audio nodes.
    if (name === 'audio') {
      const { src } = attribs;

      return <AudioPlayer src={src} />;
    }

    // Remote video nodes.
    if (name === 'div' && classes.includes('media-remote-video')) {
      const src = attribs['data-src'];

      if (src?.startsWith('https://')) {
        return <VideoPlayer controls src={src} aspectRatio="16 / 9" displayLoading={false} />;
      }

      return null;
    }

    // Iframe nodes.
    if (name === 'iframe') {
      // Fallback for existing video medias.
      if (attribs.class?.includes('media-oembed-content')) {
        return (
          <VideoPlayer controls src={attribs.src} aspectRatio="16 / 9" displayLoading={false} />
        );
      }

      return null;
    }

    // Table nodes.
    if (name === 'table') {
      const classes = attribs.class?.split(' ') ?? [];
      const tableType = getTableTypeFromClasses(classes);
      const tableProps = attributesToProps(attribs);

      return (
        <Table type={tableType} className={attribs.class} {...tableProps}>
          {domToReact(children, options)}
        </Table>
      );
    }

    // If a table cell has only text, we need to wrap it in a Text component.
    // This is required because CKEditor is too lazy to add the <p> tag.
    if (['td', 'th'].includes(name) && children.length === 1 && children[0].type === 'text') {
      const cellProps = attributesToProps(attribs);
      const CellTag = name;

      return (
        <CellTag {...cellProps}>
          <Text marginBottom="none">{domToReact(children, options)}</Text>
        </CellTag>
      );
    }

    // Separator nodes.
    if (name === 'hr') {
      const marginTop = prev === null || attribs.class?.includes('mt-0') ? undefined : 'sm';
      const marginBottom = next === null || attribs.class?.includes('mb-0') ? undefined : 'sm';

      let dash = false;
      if (classes.includes('dash')) dash = true;

      return (
        <Separator
          dash={dash}
          direction="horizontal"
          marginTop={marginTop}
          marginBottom={marginBottom}
        />
      );
    }

    // Subscript nodes.
    if (name === 'sub') {
      return <sub style={parsedStyles}>{domToReact(children, { trim: true })}</sub>;
    }

    // Superscript nodes.
    if (name === 'sup') {
      return <sup style={parsedStyles}>{domToReact(children, { trim: true })}</sup>;
    }

    // CKEditor templates.
    if (classes.includes('ckeditor-template')) {
      const templateId = classes.find((c) => c.startsWith('ckeditor-template-'));

      if (templateId) {
        const Template = getTemplate(templateId);
        if (Template) return <Template nodes={children} attribs={attribs} />;
      }
    }
  }
};

/**
 * Options for parsing HTML from Drupal.
 *
 * Note: The options are passed to the replace method recursively.
 */

export const parserOptions: HTMLReactParserOptions = {
  replace: (node) => renderReactFromHtml(node, parserOptions),
};

/**
 * Parses the HTML from Drupal and renders the correct components.
 *
 * Also remove the unnecessary whitespace from the HTML string.
 *
 * @param html - The HTML string to parse.
 *
 * @returns A tree of React components.
 */

export const parseHtml = (html: string) => {
  const trimmedHtml = html?.replace(/(\r\n|\n|\r)/gm, '') ?? '';
  return parse(trimmedHtml, parserOptions);
};

// Type definition of RichText properties.

type Props = BaseContainerProps & {
  value: string;
};

/**
 * Rich text component.
 *
 * Parses a HTML string from Drupal CKEditor and renders it with the correct components.
 */

const RichText = ({ value, className, ...props }: Props) => {
  const parsedHtml = parseHtml(value);
  const classes = clsx('rich-text', className);
  return (
    <BaseContainer className={classes} {...props}>
      {parsedHtml}
    </BaseContainer>
  );
};

export default RichText;
